import React, { useEffect } from "react";
import styled from "@emotion/styled";
import NumberInput from "@components/input/NumberInput";
import FadeInContainer from "@components/motion/FadeInContainer";
import { useFormContext } from "react-hook-form";
import LabelWithInput from "@components/input/LabelWithInput";
import { Flex } from "antd";
import { calculateAmountWithPercentage } from "@utils/functions";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const InputBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  color: theme.color.gray,
}));

const Suffix = styled.span(({ theme }) => ({
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.bold,
  lineHeight: "2rem",
  color: theme.color.black,
}));

const BusinessDesc = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.2rem",
  fontSize: "1.2rem",
  padding: "1.2rem",
  backgroundColor: theme.color.border_gray,
  border: `1px solid ${theme.color.light_gray}`,

  ".title": {
    fontWeight: theme.fontWeight.bold,
  },
  ".desc": {
    lineHeight: "1.6rem",
  },
}));

const PriceWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  lineHeight: "1.6rem",

  ".name": { color: theme.color.gray },
  ".price": {},
  ".event": { color: theme.color.green },

  ".final_name": { color: theme.color.gray, fontWeight: theme.fontWeight.bold },
  ".final_price": { fontWeight: theme.fontWeight.bold },
}));

const Line = styled.div(({ theme }) => ({
  width: "100%",
  height: "1px",
  backgroundColor: theme.color.border_gray,
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
}));

const PriceInput = () => {
  const {
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const description = watch("description_ko");
  const price = watch("price");
  const { t } = useTranslation();
  const { getProductRate } = useApiRequest();

  const { data: productRate } = useQuery({
    queryKey: ["rate"],
    queryFn: getProductRate,
  });

  const { calculatedAmount, amountAfterDeduction } =
    calculateAmountWithPercentage(price, productRate?.rate);

  useEffect(() => {
    if (price) {
      trigger("price");
    }
  }, [price, trigger]);

  return (
    <FadeInContainer isShow={description !== ""}>
      <Container>
        <LabelWithInput label="판매가격" required>
          {errors.price && errors.price.message && (
            <ErrorMessage>
              {typeof errors.price.message === "string"
                ? t(errors.price.message)
                : t("")}
            </ErrorMessage>
          )}

          <InputBox>
            <span>
              {t("입력하신 판매가격에 수수료를 뺀 금액을 정산받게 됩니다")}
            </span>
            <NumberInput
              name={`price`}
              type="number"
              mode="numeric"
              dir="rtl"
              value={isNaN(watch(`price`)) ? "" : watch(`price`)}
              placeholder="1,000">
              <Suffix> {t("원")}</Suffix>
            </NumberInput>
          </InputBox>
        </LabelWithInput>

        <BusinessDesc>
          <span className="title">{t("* 사업자 회원 안내")}</span>
          <span className="desc">
            {t(
              "사업자 회원은 정산 시 세금계산서 발행 및 부가세 와 수수료가 공제 된 금액이 입금됩니다. 실제로 정산 받고자 하는 금액을 판매가에 반드시 반영해주세요.",
            )}
          </span>
        </BusinessDesc>

        <PriceWrapper>
          <Flex justify="space-between">
            <span className="name">{t("판매가")}</span>
            <span className="price">
              {isNaN(price) ? "" : price?.toLocaleString()}
              {t("원")}
            </span>
          </Flex>
          <Flex justify="space-between">
            <span className="name">
              {t("수수료")} {productRate?.rate}%
            </span>
            <span className="price">
              {isNaN(calculatedAmount)
                ? ""
                : calculatedAmount?.toLocaleString()}
              {t("원")}
            </span>
          </Flex>

          <span className="event">
            {t("* 현재 수수료 무료 이벤트 기간입니다.")}
          </span>

          <Line />

          <Flex justify="space-between">
            <span className="final_name">{t("정산 받을 금액")}</span>
            <span className="final_price">
              {isNaN(amountAfterDeduction)
                ? ""
                : amountAfterDeduction.toLocaleString()}
              {t("원")}
            </span>
          </Flex>
        </PriceWrapper>
      </Container>
    </FadeInContainer>
  );
};

export default PriceInput;
